import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export interface TablaExport {
  nombre: string;
  encabezados: string[];
  filas: (string | number | null)[][];
}

// ---------------------------------------------------------------------------
// Logo — rasteriza el SVG oficial a PNG dataURL para usarlo en jsPDF.
// Cacheado entre llamadas (la fuente única es web/public/logo-adelante.svg).
// ---------------------------------------------------------------------------
const LOGO_URL = '/logo-adelante.svg';
let logoCache: { dataURL: string; aspectRatio: number } | null = null;

async function obtenerLogoPNG(): Promise<{ dataURL: string; aspectRatio: number } | null> {
  if (logoCache) return logoCache;
  try {
    const res = await fetch(LOGO_URL);
    if (!res.ok) return null;
    const svgText = await res.text();

    // Sacar dimensiones del viewBox para preservar aspect ratio
    const vb = /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/.exec(svgText);
    const w = vb ? Number(vb[1]) : 745;
    const h = vb ? Number(vb[2]) : 326;
    const aspectRatio = w / h;

    // Renderizar a canvas a una resolución suficientemente alta para PDF
    const renderH = 240;
    const renderW = Math.round(renderH * aspectRatio);

    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('No se pudo cargar el logo'));
        img.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = renderW;
      canvas.height = renderH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, renderW, renderH);
      logoCache = { dataURL: canvas.toDataURL('image/png'), aspectRatio };
      return logoCache;
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Excel — structured data
// ---------------------------------------------------------------------------
export function exportarExcel(nombreArchivo: string, tablas: TablaExport[]) {
  const wb = XLSX.utils.book_new();
  for (const tabla of tablas) {
    const ws = XLSX.utils.aoa_to_sheet([tabla.encabezados, ...tabla.filas]);
    ws['!cols'] = tabla.encabezados.map(() => ({ wch: 22 }));

    // Apply thousands-separator format to all numeric cells
    for (const key of Object.keys(ws)) {
      if (key.startsWith('!')) continue;
      const cell = ws[key] as XLSX.CellObject;
      if (cell.t === 'n') {
        const v = cell.v as number;
        // Integers: no decimals; floats: up to 2 decimals
        cell.z = Number.isInteger(v) ? '#,##0' : '#,##0.00';
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, tabla.nombre.slice(0, 31));
  }
  XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);
}

// ---------------------------------------------------------------------------
// PDF — capture DOM element as image, slicing into pages
// ---------------------------------------------------------------------------
export async function exportarPDF(titulo: string, subtitulo: string, elemento: HTMLElement) {
  // Esperar a que las fuentes terminen de cargar — sin esto html2canvas
  // captura con métricas de fallback y los números aparecen recortados arriba.
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  // Pre-cargar el logo en paralelo a la captura
  const logoPromesa = obtenerLogoPNG();

  // Antes de capturar, expandir contenedores con scroll para que nada quede cortado
  const scrollables = elemento.querySelectorAll<HTMLElement>('.overflow-y-auto');
  const prevStyles = Array.from(scrollables).map((el) => ({
    el,
    maxH: el.style.maxHeight,
    overflow: el.style.overflow,
  }));
  for (const el of scrollables) {
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
  }

  // Inyectar overrides temporales:
  // - `.truncate` (white-space: nowrap + overflow: hidden) recorta visualmente
  //   los descendentes/ascendentes de fuente cuando html2canvas rasteriza,
  //   especialmente en los KpiCards. Lo desactivamos durante la captura.
  // - `.overflow-hidden` también recorta a veces el texto de cards.
  const styleTag = document.createElement('style');
  styleTag.setAttribute('data-pdf-export', '');
  styleTag.textContent = `
    .pdf-export-mode .truncate {
      overflow: visible !important;
      text-overflow: clip !important;
      white-space: normal !important;
    }
    .pdf-export-mode .overflow-hidden {
      overflow: visible !important;
    }
    /* Margen extra arriba para que no se corte la línea base */
    .pdf-export-mode {
      padding-top: 4px;
    }
  `;
  document.head.appendChild(styleTag);
  elemento.classList.add('pdf-export-mode');

  // Forzar reflow tras agregar la clase para que html2canvas vea el layout nuevo
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  // Medir posiciones de page-break AHORA, después de aplicar pdf-export-mode,
  // para que coincidan con el layout que va a capturar html2canvas. Si midiéramos
  // antes, el padding-top (4px) desplazaría todo y las Y quedarían 4-6px corridas
  // y el page break caería en el lugar equivocado.
  const elementoRect = elemento.getBoundingClientRect();
  const pageBreaksCSS = Array.from(
    elemento.querySelectorAll<HTMLElement>('[data-pdf-page-break-before]'),
  )
    .map((el) => el.getBoundingClientRect().top - elementoRect.top)
    .filter((y) => y > 0)
    .sort((a, b) => a - b);

  const canvas = await html2canvas(elemento, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    // Skip elements marked as PDF-only-ignored (toolbar controls)
    ignoreElements: (el) => el.hasAttribute('data-pdf-ignore'),
  });

  // Restaurar estado original
  elemento.classList.remove('pdf-export-mode');
  styleTag.remove();
  for (const { el, maxH, overflow } of prevStyles) {
    el.style.maxHeight = maxH;
    el.style.overflow = overflow;
  }

  const logo = await logoPromesa;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentW = pageW - margin * 2;

  // ---- Header de la primera página: logo + título + subtítulo ----
  let headerH = 0;
  if (logo) {
    const logoH = 11; // mm
    const logoW = logoH * logo.aspectRatio;
    doc.addImage(logo.dataURL, 'PNG', margin, margin, logoW, logoH);
    doc.setFontSize(13);
    doc.setTextColor(20);
    doc.text(titulo, margin + logoW + 5, margin + 5);
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(subtitulo, margin + logoW + 5, margin + 10);
    doc.setTextColor(0);
    headerH = logoH + 5;
  } else {
    // Fallback sin logo
    doc.setFontSize(13);
    doc.text(titulo, margin, margin + 5);
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(subtitulo, margin, margin + 10);
    doc.setTextColor(0);
    headerH = 14;
  }

  // mm per canvas pixel — scales image to fit page width
  const mmPerPx = contentW / canvas.width;

  // Convertir los page breaks de px CSS a px de canvas (html2canvas escala x scale)
  const scaleCanvas = canvas.width / elementoRect.width;
  const pageBreaksCanvas = pageBreaksCSS
    .map((y) => Math.round(y * scaleCanvas))
    .filter((y) => y < canvas.height);

  let srcY = 0;
  let primeraPagina = true;

  while (srcY < canvas.height) {
    if (!primeraPagina) doc.addPage();

    const topMm = primeraPagina ? margin + headerH : margin;
    const availMm = pageH - topMm - margin;
    const pagePx = Math.floor(availMm / mmPerPx);
    const maxSliceH = Math.min(pagePx, canvas.height - srcY);

    // Si hay un page break forzado dentro del rango natural, recortar la
    // página actual ahí para que el contenido posterior arranque en una
    // hoja nueva.
    const proxBreak = pageBreaksCanvas.find((y) => y > srcY && y <= srcY + maxSliceH);
    const sliceH = proxBreak !== undefined ? proxBreak - srcY : maxSliceH;

    // Crop this page's slice from the canvas
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceH;
    const ctx = slice.getContext('2d');
    if (!ctx) break;
    ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    const sliceHeightMm = sliceH * mmPerPx;
    doc.addImage(slice.toDataURL('image/png'), 'PNG', margin, topMm, contentW, sliceHeightMm);

    srcY += sliceH;
    primeraPagina = false;
  }

  doc.save(`${titulo} - ${subtitulo}.pdf`);
}

// ---------------------------------------------------------------------------
// PDF fallback — table-only (used inside DataGrid which has no DOM ref)
// ---------------------------------------------------------------------------
export async function exportarPDFTablas(titulo: string, subtitulo: string, tablas: TablaExport[]) {
  const doc = new jsPDF({ orientation: 'landscape' });
  const logo = await obtenerLogoPNG();

  // ---- Header: logo + título ----
  let y: number;
  if (logo) {
    const logoH = 10; // mm
    const logoW = logoH * logo.aspectRatio;
    doc.addImage(logo.dataURL, 'PNG', 14, 12, logoW, logoH);
    doc.setFontSize(14);
    doc.text(titulo, 14 + logoW + 5, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(subtitulo, 14 + logoW + 5, 24);
    doc.setTextColor(0);
    y = 12 + logoH + 8;
  } else {
    doc.setFontSize(14);
    doc.text(titulo, 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(subtitulo, 14, 24);
    doc.setTextColor(0);
    y = 30;
  }

  for (const tabla of tablas) {
    if (tablas.length > 1) {
      doc.setFontSize(10);
      doc.text(tabla.nombre, 14, y);
      y += 5;
    }

    autoTable(doc, {
      head: [tabla.encabezados],
      body: tabla.filas.map((f) =>
        f.map((v) => {
          if (v == null) return '';
          if (typeof v === 'number') {
            return Number.isInteger(v)
              ? v.toLocaleString('en-US')
              : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
          return String(v);
        }),
      ),
      startY: y,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 64, 175] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 12;
  }

  doc.save(`${titulo} - ${subtitulo}.pdf`);
}
